import { companyRequest, CompanyRequestError } from '@/company/api';
import { formatCopy, namespaceCopyFor } from '@/i18n/ns';
import { localeLanguage } from '@/i18n/eu-locales';
import nativeConnectionsCopy from '@/i18n/ns/native-connections';
import type { ConnectionAccessChoice, ConnectionReadItem } from '../connection-read-access/api';

export type NativePostgresSetupState = {
  available: boolean;
  canManage: boolean;
  reason?: 'postgres_setup_unavailable' | 'company_admin_required';
};
export type NativePostgresConfig = {
  name: string;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  ca: string;
  servername: string;
};
export type NativePostgresTable = {
  name: string;
  kind: 'table';
  oid: number;
  columns: string[];
  write?: { insert: boolean; update: string[]; delete: boolean; primaryKey: string[] };
};
export type NativePostgresTestResult = {
  catalog: { schemas: { name: string; tables: NativePostgresTable[] }[] };
  checks: {
    tls: 'verified';
    readOnly: 'replica' | 'role' | 'application';
    supportsWrites?: boolean;
  };
  catalogHash: string;
};
export type NativePostgresDetail = {
  connectionId: string;
  bindingVersion: string;
  config: {
    name: string;
    host: string;
    port: number;
    database: string;
    username: string;
    tls?: { ca?: string; servername?: string };
  };
  resources: string[];
  checks: NativePostgresTestResult['checks'];
};

export const emptyNativePostgresConfig: NativePostgresConfig = {
  name: '',
  host: '',
  port: '5432',
  database: '',
  username: '',
  password: '',
  ca: '',
  servername: '',
};

export const nativePostgresErrorCopy: Record<string, keyof typeof nativeConnectionsCopy.en> = {
  postgres_tls_failed: 'pgTlsFailed',
  postgres_auth_failed: 'pgAuthFailed',
  postgres_network_failed: 'pgNetworkFailed',
  postgres_read_only_required: 'pgReadOnlyRequired',
  postgres_no_tables: 'pgNoTables',
  postgres_catalog_too_large: 'pgCatalogLarge',
  postgres_catalog_changed: 'pgCatalogChanged',
  postgres_unsafe_host: 'pgUnsafeHost',
  postgres_setup_busy: 'pgSetupBusy',
  invalid_request: 'invalidRequest',
  connection_changed: 'connectionChanged',
};

export function nativePostgresError(error: unknown, locale = 'en') {
  const copy = namespaceCopyFor(nativeConnectionsCopy, localeLanguage(locale));
  return error instanceof CompanyRequestError
    ? nativePostgresErrorCopy[error.code]
      ? formatCopy(copy[nativePostgresErrorCopy[error.code]], {})
      : error.message
    : copy.genericStepError;
}

export function nativePostgresConfigBody(config: NativePostgresConfig) {
  return {
    name: config.name.trim(),
    host: config.host.trim(),
    port: Number(config.port),
    database: config.database.trim(),
    username: config.username.trim(),
    password: config.password,
    tls: {
      ...(config.ca.trim() ? { ca: config.ca } : {}),
      ...(config.servername.trim() ? { servername: config.servername.trim() } : {}),
    },
  };
}

export function nativePostgresDetailConfig(
  detail: NativePostgresDetail,
  password: string,
): NativePostgresConfig {
  return {
    name: detail.config.name,
    host: detail.config.host,
    port: String(detail.config.port),
    database: detail.config.database,
    username: detail.config.username,
    password,
    ca: detail.config.tls?.ca ?? '',
    servername: detail.config.tls?.servername ?? '',
  };
}

export function nativePostgresChoice(
  item: ConnectionReadItem | null,
  audience: 'private' | 'company' = 'private',
): ConnectionAccessChoice {
  return {
    audience: item?.policy?.audience ?? item?.audience ?? audience,
    people: item?.policy?.people ?? [],
    changes: item?.supportsWrites === true ? (item?.policy?.changes ?? 'ask') : 'ask',
  };
}

export function secureRequestId() {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (!randomUUID) throw new Error('secure_random_unavailable');
  return randomUUID.call(globalThis.crypto);
}

export const nativePostgresAccessChoice = (
  choice: ConnectionAccessChoice,
  supportsWrites: boolean,
): ConnectionAccessChoice => ({ ...choice, changes: supportsWrites ? choice.changes : 'ask' });

export { companyRequest };
